import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getResellerComm } from "../../../config/LiveApi";
import { FaCalendar } from "react-icons/fa";
import { DateRangePicker } from "react-date-range";
import Spinner from "../../Spin/Spinner";
import { toast } from "react-toastify";

const ResellerDashboard = () => {
  const token = useSelector((store) => store.userReducer.token);
  const resellerId = useSelector((store) => store.userReducer.userId);

  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  const [state, setState] = useState({
    selection: {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  });

  /* ---------------- Date Helpers ---------------- */

  const getStartOfDayISO = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  };

  const getEndOfDayISO = (date) => {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d.toISOString();
  };

  /* ---------------- Fetch Commission ---------------- */

  const fetchCommission = async () => {
    try {
      setLoading(true);

      const start = getStartOfDayISO(state.selection.startDate);
      const end = getEndOfDayISO(state.selection.endDate);

      const response = await getResellerComm(resellerId, start, end, token);

      if (response.status) {
        setMerchants(response.data || []);
      } else {
        toast.error(response.message || "No data found");
      }
    } catch (err) {
      toast.error("Failed to fetch commission");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchCommission();
  }, [token]);

  /* ---------------- Date Range Filter ---------------- */

  const isRangeWithinOneMonth = (start, end) => {
    const diffTime = new Date(end) - new Date(start);
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    return diffDays <= 31;
  };

  /* ---------------- Totals Calculation ---------------- */

  const totalPayin = merchants.reduce(
    (sum, m) => sum + Number(m.totalPayin || 0),
    0
  );

  const totalPayout = merchants.reduce(
    (sum, m) => sum + Number(m.totalPayout || 0),
    0
  );

  const totalPayinCommission = merchants.reduce(
    (sum, m) => sum + Number(m.payinCommission || 0),
    0
  );

  const totalPayoutCommission = merchants.reduce(
    (sum, m) => sum + Number(m.payoutCommission || 0),
    0
  );

  const netCommission = totalPayinCommission + totalPayoutCommission;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.title}>Dashboard</div>

        <div style={{ position: "relative" }}>
          <button
            onClick={() => setShowPicker(!showPicker)}
            style={styles.calendarButton}
          >
            <FaCalendar />
          </button>

          {showPicker && (
            <div style={styles.pickerWrapper}>
              <DateRangePicker
                ranges={[state.selection]}
                onChange={(item) => {
                  const { startDate, endDate } = item.selection;

                  if (!isRangeWithinOneMonth(startDate, endDate)) {
                    toast.error("You can select maximum 1 month date range");
                    return;
                  }

                  setState({ selection: item.selection });
                }}
                maxDate={new Date()}
              />

              <button
                style={styles.applyButton}
                onClick={async () => {
                  setShowPicker(false);
                  await fetchCommission();
                }}
              >
                Apply
              </button>
            </div>
          )}
        </div>
      </div>

      <div style={styles.selectedRange}>
        Selected Date Range:{" "}
        <strong>
          {new Date(state.selection.startDate).toLocaleDateString("en-GB")} -{" "}
          {new Date(state.selection.endDate).toLocaleDateString("en-GB")}
        </strong>
      </div>

      {loading && <Spinner />}

      {!loading && (
        <>
          {/* Summary Cards */}

          <div style={styles.cardContainer}>
            <div style={styles.card}>
              <div style={styles.cardTitle}>Total Payin</div>
              <div style={styles.cardValue}>₹{totalPayin.toFixed(2)}</div>
            </div>

            <div style={styles.card}>
              <div style={styles.cardTitle}>Total Payout</div>
              <div style={styles.cardValue}>₹{totalPayout.toFixed(2)}</div>
            </div>

            <div style={styles.card}>
              <div style={styles.cardTitle}>Payin Commission</div>
              <div style={styles.cardValue}>
                ₹{totalPayinCommission.toFixed(2)}
              </div>
            </div>

            <div style={styles.card}>
              <div style={styles.cardTitle}>Payout Commission</div>
              <div style={styles.cardValue}>
                ₹{totalPayoutCommission.toFixed(2)}
              </div>
            </div>

            <div style={styles.card}>
              <div style={styles.cardTitle}>Net Commission</div>
              <div style={styles.cardValue}>₹{netCommission.toFixed(2)}</div>
            </div>
          </div>

          {/* Merchant Table */}

          {merchants.length > 0 && (
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Merchant</th>
                    <th style={styles.th}>Payin</th>
                    <th style={styles.th}>Payout</th>
                    <th style={styles.th}>Payin Commission</th>
                    <th style={styles.th}>Payout Commission</th>
                  </tr>
                </thead>

                <tbody>
                  {merchants.map((m) => (
                    <tr key={m.merchantId}>
                      <td style={styles.td}>{m.merchantName}</td>
                      <td style={styles.td}>₹{m.totalPayin}</td>
                      <td style={styles.td}>₹{m.totalPayout}</td>
                      <td style={styles.td}>₹{m.payinCommission}</td>
                      <td style={styles.td}>₹{m.payoutCommission}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: "30px",
    backgroundColor: "#f4f6f9",
    minHeight: "100vh",
    fontFamily: "Segoe UI, sans-serif",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "25px",
  },

  title: {
    fontSize: "24px",
    fontWeight: "600",
    color: "#1f2937",
  },

  calendarButton: {
    padding: "10px 14px",
    backgroundColor: "#fc8019",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
  },

  pickerWrapper: {
    position: "absolute",
    top: "60px",
    right: "0",
    zIndex: 1000,
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
    padding: "15px",
  },

  applyButton: {
    marginTop: "10px",
    padding: "8px 16px",
    backgroundColor: "#fc8019",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },

  selectedRange: {
    marginBottom: "20px",
    fontSize: "14px",
    color: "#4b5563",
  },

  cardContainer: {
    display: "flex",
    gap: "20px",
    marginBottom: "25px",
    flexWrap: "wrap",
  },

  card: {
    flex: "1",
    minWidth: "220px",
    backgroundColor: "#fff",
    padding: "20px",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
  },

  cardTitle: {
    fontSize: "14px",
    color: "#6b7280",
    marginBottom: "8px",
  },

  cardValue: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#111827",
  },

  tableWrapper: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
    overflow: "hidden",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
  },

  th: {
    backgroundColor: "#f9fafb",
    textAlign: "left",
    padding: "14px",
    fontSize: "14px",
    color: "#374151",
    borderBottom: "1px solid #e5e7eb",
  },

  td: {
    padding: "14px",
    fontSize: "14px",
    borderBottom: "1px solid #f1f1f1",
  },
};

export default ResellerDashboard;